// src/pages/Admin/AdminUsers/AdminUsers.jsx
import React, { useState, useEffect, useContext } from 'react';
import AdminSidebar from '../../../components/AdminSidebar/AdminSidebar';
import Modal from '../../../components/Modal/Modal';
import { FaEdit, FaTrash, FaKey } from 'react-icons/fa';
import './AdminUsers.css';
import { AuthContext } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config/api';

const AdminUsers = () => {
  const { hasPermission, authToken, logout, user, permissions, userId } = useContext(AuthContext);

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    status: 'active',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Para cambiar contraseña
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError('Error al cargar usuarios: ' + err.message);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/roles`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }
      const data = await response.json();
      setRoles(data);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    // Depuración: imprime permisos
    // console.log('Permisos del usuario:', permissions);
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditClick = (user) => {
    setEditingUser({ ...user, discord_id: user.discord_id || '' });
    setIsEditModalOpen(true);
    setShowPasswordField(false);
    setNewPassword('');
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setShowPasswordField(false);
    setNewPassword('');
    setEditingUser(null);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!hasPermission('editar_usuario')) {
      alert('No tienes permiso para editar usuarios.');
      return;
    }
    try {
      setLoading(true);
      const body = { ...editingUser };
      // Solo envía password si hay valor y es diferente de vacío
      if (showPasswordField && newPassword.trim().length > 0) {
        body.password = newPassword;
      } else {
        delete body.password;
      }
      const response = await fetch(`${API_BASE_URL}/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await fetchUsers();
      closeEditModal();
    } catch (err) {
      setError('Error al actualizar usuario: ' + err.message);
      console.error('Error updating user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!hasPermission('borrar_usuario')) {
      alert('No tienes permiso para eliminar usuarios.');
      return;
    }
    if (window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/users/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        setUsers(users.filter((user) => user.id !== id));
        if (editingUser && editingUser.id === id) closeEditModal();
      } catch (err) {
        setError('Error al eliminar usuario: ' + err.message);
        console.error('Error deleting user:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleNewUserInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!hasPermission('crear_usuario')) {
      alert('No tienes permiso para crear usuarios.');
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(newUser),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      fetchUsers();
      setIsAddUserModalOpen(false);
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: 'user',
        status: 'active',
      });
    } catch (err) {
      setError('Error al añadir usuario: ' + err.message);
      console.error('Error adding user:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-users-container">
      <AdminSidebar />
      <div className="admin-content">
        <h1 className="admin-page-title">Administración de Usuarios</h1>
        {error && <p className="error-message">{error}</p>}
        {loading ? (
          <p className="loading-message">Cargando usuarios...</p>
        ) : (
          <div className="admin-section">
            <h2 className="section-title">Lista de Usuarios</h2>
            <div className="list-header-controls">
              <input
                type="text"
                placeholder="Buscar por usuario, email o rol..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
              {hasPermission('crear_usuario') && (
                <button
                  onClick={() => setIsAddUserModalOpen(true)}
                  className="add-new-user-button"
                >
                  Añadir Nuevo Usuario
                </button>
              )}
            </div>
            <div className="table-responsive">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Discord ID</th>
                    <th>Rol</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td style={{fontSize:'0.85rem',color:'#9aa0a6'}}>{user.discord_id || <span style={{color:'#555'}}>—</span>}</td>
                        <td>{user.role}</td>
                        <td>{user.status === 'active' ? 'Activo' : 'Inactivo'}</td>
                        <td>
                          <div className="action-buttons">
                            {hasPermission('editar_usuario') && (
                              <button
                                onClick={() => handleEditClick(user)}
                                className="action-button icon-button edit-button"
                                title="Editar"
                              >
                                <FaEdit />
                              </button>
                            )}
                            {hasPermission('borrar_usuario') && user.id !== userId && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="action-button icon-button delete-button"
                                title="Borrar"
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                        <td colSpan="7" className="no-results">
                        No se encontraron usuarios.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal para Editar Usuario */}
        {isEditModalOpen && editingUser && (
          <Modal onClose={closeEditModal}>
            <h2 className="modal-title">Editar Usuario</h2>
            <form onSubmit={handleUpdateUser} className="modal-form">
              <div className="form-group">
                <label htmlFor="edit-username">Usuario:</label>
                <input
                  type="text"
                  id="edit-username"
                  name="username"
                  value={editingUser.username}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, username: e.target.value })
                  }
                  readOnly={!hasPermission('editar_usuario')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-email">Email:</label>
                <input
                  type="email"
                  id="edit-email"
                  name="email"
                  value={editingUser.email}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, email: e.target.value })
                  }
                  readOnly={!hasPermission('editar_usuario')}
                />
              </div>
              <div className="form-group">
                <label>Cambiar Contraseña:</label>
                {hasPermission('editar_usuario') && !showPasswordField ? (
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => setShowPasswordField(true)}
                    style={{ backgroundColor: '#ffc107', color: '#222', marginBottom: 8 }}
                  >
                    <FaKey style={{ marginRight: 6 }} />
                    Cambiar Contraseña
                  </button>
                ) : (
                  showPasswordField && (
                    <>
                      <input
                        type="password"
                        placeholder="Nueva contraseña"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        minLength={6}
                        style={{ width: '100%', marginBottom: 8 }}
                      />
                      <button
                        type="button"
                        className="action-button secondary-button"
                        onClick={() => { setShowPasswordField(false); setNewPassword(''); }}
                        style={{ marginLeft: 8 }}
                      >
                        Cancelar
                      </button>
                    </>
                  )
                )}
              </div>
              <div className="form-group">
                <label htmlFor="edit-discord-id">Discord ID (para login con Discord):</label>
                <input
                  type="text"
                  id="edit-discord-id"
                  name="discord_id"
                  value={editingUser.discord_id || ''}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, discord_id: e.target.value })
                  }
                  placeholder="Ej: 123456789012345678"
                  readOnly={!hasPermission('editar_usuario')}
                />
                <small style={{ color: '#888', fontSize: '0.8rem' }}>Vincula tu Discord ID para poder acceder al panel de admin usando Discord OAuth.</small>
              </div>
              <div className="form-group">
                <label htmlFor="edit-role">Rol:</label>
                {hasPermission('editar_usuario') ? (
                  <select
                    id="edit-role"
                    name="role"
                    value={editingUser.role}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, role: e.target.value })
                    }
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.name}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="read-only-field">{editingUser.role}</p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="edit-status">Estado:</label>
                {hasPermission('editar_usuario') ? (
                  <select
                    id="edit-status"
                    name="status"
                    value={editingUser.status}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, status: e.target.value })
                    }
                  >
                    <option value="active">Activado</option>
                    <option value="inactive">Desactivado</option>
                  </select>
                ) : (
                  <p className="read-only-field">{editingUser.status}</p>
                )}
              </div>
              <div className="form-actions">
                {hasPermission('editar_usuario') && (
                  <button type="submit" className="action-button success-button">
                    Guardar Cambios
                  </button>
                )}
                {hasPermission('borrar_usuario') && (
                  <button
                    type="button"
                    className="action-button delete-button"
                    onClick={() => handleDeleteUser(editingUser.id)}
                  >
                    Borrar Usuario
                  </button>
                )}
                <button
                  type="button"
                  className="action-button secondary-button"
                  onClick={closeEditModal}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </Modal>
        )}

        {/* Modal para Añadir Usuario */}
        {isAddUserModalOpen && (
          <Modal onClose={() => setIsAddUserModalOpen(false)}>
            <h2 className="modal-title">Añadir Nuevo Usuario</h2>
            <form onSubmit={handleAddUser} className="modal-form">
              <div className="form-group">
                <label htmlFor="new-username">Usuario:</label>
                <input
                  type="text"
                  id="new-username"
                  name="username"
                  value={newUser.username}
                  onChange={handleNewUserInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-email">Email:</label>
                <input
                  type="email"
                  id="new-email"
                  name="email"
                  value={newUser.email}
                  onChange={handleNewUserInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-password">Contraseña:</label>
                <input
                  type="password"
                  id="new-password"
                  name="password"
                  value={newUser.password}
                  onChange={handleNewUserInputChange}
                  minLength={6}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-role">Rol:</label>
                <select
                  id="new-role"
                  name="role"
                  value={newUser.role}
                  onChange={handleNewUserInputChange}
                >
                  {roles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="new-status">Estado:</label>
                <select
                  id="new-status"
                  name="status"
                  value={newUser.status}
                  onChange={handleNewUserInputChange}
                >
                  <option value="active">Activado</option>
                  <option value="inactive">Desactivado</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="action-button success-button">
                  Añadir Usuario
                </button>
                <button
                  type="button"
                  className="action-button secondary-button"
                  onClick={() => setIsAddUserModalOpen(false)}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;